import React, { useRef, useState } from "react";

import { TypeFiesta, TypeMini } from "@at/design-system-foundation";

import { GalleryModalSliderImage } from "./gallery-modal-slider-images";
import * as Styled from "./gallery-modal-slider.styled";
import { GalleryModalSliderImage as StyledGalleryModalSliderImage } from "./gallery-modal-slider.styled";
import { useLeasingStateContext } from "../../../contexts/leasing/context/leasing.context";
import { getDisplayPrice } from "../../../utils/formatter/formatter.utils";
import { useAdvert } from "../../../utils/hooks/use-advert";
import { useChannel } from "../../../utils/hooks/use-channel";
import {
  comScorePageViewTrack,
  ImageGalleryState,
} from "../../../utils/tracking/tracking-data-utils";
import { useLeasingData } from "../../leasing/contract-customiser/hooks/useLeasingData/use-leasing-data.hook";
import { useLeasingQueries } from "../../leasing/contract-customiser/hooks/useLeasingQueries/use-leasing-queries.hook";
import GalleryCloseButton from "../gallery-close/gallery-close";
import {
  GalleryImage,
  ImageInViewHandler,
} from "../gallery-image/gallery-image";
import { GalleryIconBar as StyledGalleryIconBar } from "../gallery-modal/gallery-modal.styled";
import { GalleryAsset, groupByCategory } from "../gallery.utils";
import { Slider } from "../slider/slider";

import { GallerySliderImage as StyledGallerySliderImage } from "../slider/slider.styled";

export interface GalleryModalSliderProps {
  slide: number;
  images: GalleryAsset[];
  returnToGalleryModalGrid: () => void;
  returnToProductPage?: () => void;
  isLeasingPDPPage?: boolean;
  hasVideo: boolean;
  hasSpin: boolean;
  activeCategory: string;
  handleImageTracked: (
    imageId: string,
    index: number,
    type: ImageGalleryState,
  ) => void;
}

const parseGalleryTitle = ({
  slide,
  totalImages,
}: {
  slide: number;
  totalImages: number;
}) => {
  return slide === totalImages ? "" : `${slide + 1} of ${totalImages}`;
};

export const GalleryModalSlider = ({
  slide,
  images,
  returnToGalleryModalGrid,
  returnToProductPage,
  isLeasingPDPPage,
  hasVideo,
  hasSpin,
  activeCategory,
  handleImageTracked,
}: GalleryModalSliderProps) => {
  const [galleryTitle, setGalleryTitle] = useState(
    parseGalleryTitle({
      slide,
      totalImages: images.length,
    }),
  );
  const modalRef = useRef<HTMLDivElement | null>(null);

  const skipGridView =
    images &&
    (isLeasingPDPPage ? images.length < 4 : images.length < 6) &&
    !hasVideo &&
    !hasSpin;

  const onImageInView: ImageInViewHandler = ({ index, src }) => {
    const imageId = src.match(/([\dA-Za-z]+)\.jpg/)?.[1] || src;
    handleImageTracked(imageId, index, ImageGalleryState.in_slider);
    comScorePageViewTrack();
  };

  const channel = useChannel();
  const advert = useAdvert();
  const displayPrice = getDisplayPrice(advert, channel);
  const title = advert.heading?.title;
  const showActiveCategory = !isLeasingPDPPage;

  const groupedImages = groupByCategory(images);

  const state = useLeasingStateContext();
  const { productPageData } = useLeasingQueries();

  const { productData } = state;
  const leasingAdvert = productData ?? productPageData?.search.advert;
  const { monthlyPrice } = useLeasingData();
  const vehicleMake = leasingAdvert?.specification?.make || "";
  const vehicleModel = leasingAdvert?.specification?.model || "";

  const onSliderUpdate = (slide: number) => {
    setGalleryTitle(
      parseGalleryTitle({
        slide,
        totalImages: groupedImages[activeCategory]?.length || 0,
      }),
    );
  };

  return (
    <div ref={modalRef}>
      <StyledGalleryIconBar onSliderModal>
        <GalleryCloseButton
          closeFn={
            skipGridView ? returnToProductPage : returnToGalleryModalGrid
          }
        />
      </StyledGalleryIconBar>
      <Styled.GallerySliderWrapper>
        <Slider
          {...{
            initialSlide: slide,
            onSliderUpdate,
            showImageCount: false,
          }}
        >
          {groupedImages[activeCategory]?.map((image, index) => {
            const src = image.image.url;
            const alt = image.image.alt;
            return (
              <StyledGallerySliderImage
                key={`${image?.type}-${image?.image?.url}`}
              >
                <StyledGalleryModalSliderImage>
                  <GalleryImage
                    {...{
                      src,
                      index,
                      onInView: onImageInView,
                    }}
                  >
                    <GalleryModalSliderImage
                      src={src}
                      alt={alt}
                      shouldLazyLoad={index !== 0}
                    />
                  </GalleryImage>
                </StyledGalleryModalSliderImage>
              </StyledGallerySliderImage>
            );
          })}
        </Slider>
        <Styled.GalleryDescription>
          <TypeFiesta weight="600">{`${
            showActiveCategory ? activeCategory : "Image"
          }: ${galleryTitle}`}</TypeFiesta>
          {isLeasingPDPPage ? (
            <>
              <TypeFiesta>{`${vehicleMake} ${vehicleModel}`}</TypeFiesta>
              <TypeFiesta weight="600">
                {monthlyPrice} <TypeMini as="span">per month</TypeMini>
              </TypeFiesta>
            </>
          ) : (
            <>
              <TypeFiesta>{title}</TypeFiesta>
              <TypeFiesta weight="600">
                <span>{displayPrice}</span>
              </TypeFiesta>
            </>
          )}
        </Styled.GalleryDescription>
      </Styled.GallerySliderWrapper>
    </div>
  );
};
